import { useState } from 'react';
import { Users, BarChart3, Settings, Calendar } from 'lucide-react';
import { useAdmin } from '../hooks/useAdmin';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Alert from '../components/ui/Alert';
import UserManagement from '../components/admin/UserManagement';
import Analytics from '../components/admin/Analytics';
import ExtensionManager from '../components/admin/ExtensionManager';
import AdminSettings from '../components/admin/AdminSettings';

export default function AdminDashboard() {
  const { isAdmin, loading } = useAdmin();
  const [activeTab, setActiveTab] = useState('users');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Alert variant="error">
          You do not have permission to access the admin dashboard.
        </Alert>
      </div>
    );
  }

  const tabs = [
    { id: 'users', label: 'Users & Progress', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'extensions', label: 'Extensions', icon: Calendar },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">🛡️ Admin Dashboard</h1>
          <p className="mt-1 text-gray-600">Manage users and monitor system health</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop tabs */}
          <nav className="hidden md:flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                    ${isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* Mobile stacked buttons */}
          <div className="md:hidden py-4 grid grid-cols-2 gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium text-sm
                    ${isActive
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'analytics' && <Analytics />}
        {activeTab === 'extensions' && <ExtensionManager />}
        {activeTab === 'settings' && <AdminSettings />}
      </div>
    </div>
  );
}
